import { defineStore } from 'pinia';
import { useNuxtApp } from "#app";
import { setMeta } from "~/utils/meta";
import { ArticleCollectionType } from "~/types/article/ArticleCollectionType";
import { ArticleCategoryType } from "~/types/article/ArticleCategoryType";
import { ArticlePreviewType } from "~/types/article/ArticlePreviewType";
import { useRoute } from "vue-router";

export const useArticleCollectionStore = defineStore({
    id: 'articleCollection',
    state: (): ArticleCollectionType => ({
        category: undefined,
        fixTopList: undefined,
        list: undefined,
        pagination: undefined,
        error: undefined,
        skeleton: false,
    }),
    getters: {},
    actions: {
        clear() {
            this.category = undefined;
            this.fixTopList = undefined;
            this.list = undefined;
            this.pagination = undefined;
            this.error = undefined;
        },

        enableSkeleton() {
            this.skeleton = true;
        },

        async fetchCategory(categorySlug: string) {
            const route = useRoute();
            const config = useRuntimeConfig();

            try {
                const response = await useNuxtApp().axios.get('/interesting-facts-categories', {
                    params: {
                        fields: ['title', 'text_before_content', 'text_after_content'],
                        'populate[seo][populate][0]': 'metaImage',
                        'filters[slug][$eq]': categorySlug,
                    }
                });

                if (response?.data?.data === undefined || !response?.data?.data.length || this.error !== undefined) {
                    this.category = undefined;
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                const data = response.data.data[0];
                this.category = <ArticleCategoryType>{
                    title: data?.attributes?.seo?.metaTitle !== undefined ? data?.attributes?.seo?.metaTitle : data?.attributes?.title,
                    textBeforeContent: data?.attributes?.text_before_content,
                    textAfterContent: data?.attributes?.text_after_content,
                };

                if (data?.attributes?.seo) {
                    setMeta(data?.attributes?.seo, config, route);
                }
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        },

        async fetchFixTopList(categorySlug: string) {
            const config = useRuntimeConfig();

            try {
                const response = await useNuxtApp().axios.get(`/interesting-facts/fix-top/${categorySlug}`);

                if (response?.data?.data === undefined || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                if (!response?.data?.data.length) {
                    this.fixTopList = [];
                    return true;
                }

                this.fixTopList = response?.data?.data.map((item) => {
                    return <ArticlePreviewType>{
                        slug: item?.attributes?.slug,
                        title: item?.attributes?.title,
                        date: item?.attributes?.publishedAt,
                        picture: config.public.ssrMediaBaseUrl + item?.attributes?.picture?.data?.attributes?.url,
                        url: `/interesting-facts/fact-${item?.attributes?.slug}`
                    };
                });
                this.error = undefined;
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        },

        async fetchList(page: number, categorySlug?: string) {
            const pageSize = 12;
            const config = useRuntimeConfig();
            this.enableSkeleton();

            try {
                const params = {
                    fields: ['slug', 'title', 'publishedAt'],
                    sort: ['sorting:desc', 'publishedAt:desc'],
                    populate: ['picture'],
                    'pagination[page]': page,
                    'pagination[pageSize]': pageSize,
                };

                if (categorySlug !== undefined) {
                    params['filters[interesting_facts_category][slug][$eq]'] = categorySlug;
                } else {
                    params.populate.push('interesting_facts_category');
                }

                const response = await useNuxtApp().axios.get('/interesting-facts', {params});

                if (response?.data?.data === undefined || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    this.pagination = undefined;
                    this.skeleton = false;
                    return false;
                }

                if (!response?.data?.data.length) {
                    this.list = [];
                    this.skeleton = false;
                    this.pagination = undefined;
                    return true;
                }

                this.list = response?.data?.data.map((item) => {
                    return <ArticlePreviewType>{
                        slug: item?.attributes?.slug,
                        title: item?.attributes?.title,
                        date: item?.attributes?.publishedAt,
                        category: item?.attributes?.interesting_facts_category?.data?.attributes?.title,
                        picture: config.public.ssrMediaBaseUrl + item?.attributes?.picture?.data?.attributes?.url,
                        url: `/interesting-facts/fact-${item?.attributes?.slug}`
                    };
                });
                this.pagination = {
                    page: response?.data?.meta?.pagination?.page,
                    pageCount: response?.data?.meta?.pagination?.pageCount,
                };
                this.skeleton = false;
                this.error = undefined;
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        }
    },
});
